import type {
  UserSettings,
  UserDiscoverFeedConfig,
  FollowConfig,
  NotificationPolicy,
  NotificationPrefs,
  PrivacyConfig,
  UserTranslationConfig,
} from '@wellesley/openapi'

export type NestedUserSettings = {
  user: {
    discoverFeed?: UserDiscoverFeedConfig
    follow?: FollowConfig
    notifications: {
      policy?: NotificationPolicy
      prefs?: NotificationPrefs
    }
    privacy?: PrivacyConfig
    translation?: UserTranslationConfig
  }
}

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P]
}

export type NestedUserSettingsUpdate = DeepPartial<NestedUserSettings>

type NestedObject = Record<string, unknown>

const FLAT_KEYS = new Set([
  'user.discoverFeed',
  'user.follow',
  'user.notifications.policy',
  'user.notifications.prefs',
  'user.privacy',
  'user.translation',
])

const setNestedValue = (obj: NestedObject, path: string[], value: unknown) => {
  let current = obj
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]
    if (!(key in current)) {
      current[key] = {}
    }
    current = current[key] as NestedObject
  }
  current[path[path.length - 1]] = value
}

const flattenObject = (
  obj: NestedObject,
  prefix: string[] = [],
  result: Record<string, unknown> = {},
): Record<string, unknown> => {
  for (const key in obj) {
    const value = obj[key]
    const path = [...prefix, key]
    const flatKey = path.join('.')

    if (FLAT_KEYS.has(flatKey)) {
      result[flatKey] = value
    } else if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value)
    ) {
      flattenObject(value as NestedObject, path, result)
    }
  }
  return result
}

export const flatToNestedSettings = (
  flat: UserSettings | undefined,
): NestedUserSettings | undefined => {
  if (!flat) return undefined

  const nested: NestedObject = {}

  for (const flatKey in flat) {
    const path = flatKey.split('.')
    const value = flat[flatKey as keyof UserSettings]
    if (value !== undefined) {
      setNestedValue(nested, path, value)
    }
  }

  return nested as NestedUserSettings
}

export const nestedToFlatSettings = (
  nested: NestedUserSettingsUpdate,
): Partial<UserSettings> => {
  if (!nested) return {}

  return flattenObject(nested as NestedObject) as Partial<UserSettings>
}
